// src/features/device/deviceSlice.ts

import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/**
 * WHY THIS EXISTS:
 * We need to store device type (desktop/mobile) in Redux so that:
 * 1. Any component can access it without prop drilling
 * 2. Device type persists across component renders
 * 3. We can use it in selectors and conditional logic
 * 4. It's a single source of truth for device type
 */

// Define device types - only desktop and mobile (no tablet)
export type DeviceType = "desktop" | "mobile";

interface DeviceState {
  // Current device type - null until detected
  type: DeviceType | null;

  // Flag to show loading state during detection
  isDetecting: boolean;

  // Breakpoint configuration (can be adjusted if needed)
  breakpoints: {
    mobile: number; // Below this = mobile (768px)
    desktop: number; // Above this = desktop (769px)
  };
}

const initialState: DeviceState = {
  type: null,
  isDetecting: true, // Start as true because we need to detect first
  breakpoints: {
    mobile: 768,
    desktop: 769,
  },
};

/**
 * WHY WE USE createSlice:
 * - Redux Toolkit's createSlice automatically generates action creators
 * - Provides immutable state updates using Immer under the hood
 * - Reduces boilerplate compared to traditional Redux
 */
const deviceSlice = createSlice({
  name: "device",
  initialState,
  reducers: {
    /**
     * WHY: Called when device type is detected via useMediaQuery
     * This is the main action that sets the device type
     */
    setDeviceType: (state, action: PayloadAction<DeviceType>) => {
      state.type = action.payload;
      state.isDetecting = false; // Detection complete
    },

    /**
     * WHY: Allows manual reset if needed (e.g., for testing)
     * Usually not needed in production
     */
    resetDeviceDetection: (state) => {
      state.type = null;
      state.isDetecting = true;
    },

    /**
     * WHY: Allows runtime breakpoint adjustments (future feature)
     * Not commonly used but good for flexibility
     */
    updateBreakpoints: (
      state,
      action: PayloadAction<{ mobile?: number; desktop?: number }>
    ) => {
      if (action.payload.mobile !== undefined) {
        state.breakpoints.mobile = action.payload.mobile;
      }
      if (action.payload.desktop !== undefined) {
        state.breakpoints.desktop = action.payload.desktop;
      }
    },
  },
});

// Export actions for use in components
export const { setDeviceType, resetDeviceDetection, updateBreakpoints } =
  deviceSlice.actions;

/**
 * WHY SELECTORS:
 * Selectors provide a clean way to access state
 * They can be reused across components
 * They work with TypeScript to provide type safety
 */

// Select the device type
export const selectDeviceType = (state: any): DeviceType | null =>
  state.device.type;

// Select if we're still detecting
export const selectIsDetecting = (state: any): boolean =>
  state.device.isDetecting;

// Select if current device is mobile
export const selectIsMobile = (state: any): boolean =>
  state.device.type === "mobile";

// Select if current device is desktop
export const selectIsDesktop = (state: any): boolean =>
  state.device.type === "desktop";

// Select breakpoint configuration
export const selectBreakpoints = (state: any) => state.device.breakpoints;

// Export reducer to be added to store
export default deviceSlice.reducer;
